<template>
  <div
    class="v2026-fab-root"
    v-if="actions && actions.length"
    @keydown.esc="onEscape"
  >
    <div class="v2026-fab-wrapper" :class="{ 'is-collapsed': collapsed }" id="v2026-fab">
      <!-- Dock before list in the DOM = correct tab order; CSS `order` paints the list on top. -->
      <div class="v2026-fab-dock">
        <transition name="v2026-fab-handle">
        <button
          v-if="actionsOpen"
          type="button"
          class="v2026-fab-handle"
          ref="handle"
          aria-expanded="true"
          aria-controls="v2026-fab"
          :aria-label="copy.handleLabel"
          :title="copy.handleLabel"
          @click="collapse()"
        >
          <span class="v2026-fab-handle-glyph"><i class="fas fa-chevron-right" aria-hidden="true"></i></span>
        </button>
        </transition>

        <button
          type="button"
          class="v2026-fab-main"
          ref="main"
          :class="{ 'is-open': actionsOpen }"
          :style="{ backgroundColor: bgColor }"
          :aria-expanded="String(actionsOpen)"
          aria-controls="v2026-fab-actions"
          aria-haspopup="true"
          :aria-label="mainLabel"
          :title="copy.mainLabel"
          @click="toggleActions()"
        >
          <span class="v2026-fab-main-glyph"><i class="fas fa-plus" aria-hidden="true"></i></span>
        </button>
      </div>

      <transition-group
        tag="ul"
        name="v2026-fab-pill"
        class="v2026-fab-actions"
        id="v2026-fab-actions"
        role="group"
        :aria-label="copy.actionGroupLabel"
      >
        <li
          class="v2026-fab-actions-item"
          v-for="(action, index) in visibleActions"
          :key="action.name"
          :style="{ transitionDelay: pillDelay(index) }"
        >
          <button
            type="button"
            class="v2026-fab-action"
            :style="{ backgroundColor: action.color || bgColor }"
            :aria-label="action.tooltip"
            @click="chooseAction(action)"
          >
            <span class="v2026-fab-action-glyph"><i :class="action.icon" aria-hidden="true"></i></span>
          </button>
          <span class="v2026-fab-tooltip" aria-hidden="true">{{ action.tooltip }}</span>
        </li>
      </transition-group>
    </div>

    <button
      type="button"
      class="v2026-fab-sliver"
      ref="sliver"
      v-if="collapsed"
      aria-expanded="false"
      aria-controls="v2026-fab"
      :aria-label="copy.sliverLabel"
      :title="copy.sliverLabel"
      @click="expand()"
    >
      <span class="v2026-fab-sliver-glyph"><i class="fas fa-chevron-left" aria-hidden="true"></i></span>
    </button>
    <span class="sr-only" aria-live="polite">{{ liveMessage }} </span>
  </div>
</template>

<script>
const FAB_COPY = {
  mainLabel: 'เพิ่มข้อมูล',
  mainLabelOpen: 'ปิดเมนู',
  handleLabel: 'ซ่อนปุ่มลัด',
  sliverLabel: 'แสดงปุ่มลัด',
  actionGroupLabel: 'เมนูเพิ่มข้อมูล',
  liveCollapsed: 'ซ่อนปุ่มลัดแล้ว',
  liveExpanded: 'แสดงปุ่มลัดแล้ว'
}

const STORAGE_KEY = 'beneat-chat.ui.fab.collapsed'

export default {
  name: 'CollapsibleFab',

  props: {
    actions: {
      type: Array,
      default: () => []
    },
    bgColor: {
      type: String,
      default: '#E91E63'
    },
    position: {
      type: String,
      default: 'bottom-right'
    }
  },

  data () {
    return {
      collapsed: false,
      actionsOpen: false,
      liveMessage: '',
      copy: FAB_COPY
    }
  },

  computed: {
    visibleActions () {
      return this.actionsOpen ? this.actions : []
    },
    mainLabel () {
      return this.actionsOpen ? FAB_COPY.mainLabelOpen : FAB_COPY.mainLabel
    }
  },

  created () {
    this.collapsed = this.readCollapsed()
  },

  mounted () {
    document.addEventListener('click', this.onDocumentClick)
  },

  beforeDestroy () {
    document.removeEventListener('click', this.onDocumentClick)
  },

  methods: {
    readCollapsed () {
      let raw = null
      try {
        raw = window.localStorage.getItem(STORAGE_KEY)
      } catch (e) {
        return true // เดิม false — อ่าน storage ไม่ได้ (เช่น private mode) ก็ให้หุบ
      }
      return raw !== '0'
    },

    writeCollapsed (collapsed) {
      try {
        window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0')
      } catch (e) {
      }
    },

    toggleActions () {
      this.actionsOpen = !this.actionsOpen
    },

    chooseAction (action) {
      this.$emit(action.name)
      this.actionsOpen = false
    },

    onDocumentClick (event) {
      if (!this.actionsOpen || !this.$el) {
        return
      }
      if (!this.$el.contains(event.target)) {
        this.actionsOpen = false
      }
    },

    onEscape () {
      if (!this.actionsOpen) {
        return
      }
      this.actionsOpen = false
      this.$nextTick(() => {
        if (this.$refs.main) {
          this.$refs.main.focus()
        }
      })
    },

    collapse () {
      if (this.collapsed) {
        return
      }
      this.actionsOpen = false
      this.$nextTick(() => {
        this.collapsed = true
        this.writeCollapsed(true)
        this.liveMessage = FAB_COPY.liveCollapsed
        this.$nextTick(() => {
          if (this.$refs.sliver) {
            this.$refs.sliver.focus()
          }
        })
      })
    },

    expand () {
      if (!this.collapsed) {
        return
      }
      this.collapsed = false
      this.actionsOpen = false
      this.writeCollapsed(false)
      this.liveMessage = FAB_COPY.liveExpanded
      this.$nextTick(() => {
        if (this.$refs.main) {
          this.$refs.main.focus()
        }
      })
    },

    pillDelay (index) {
      const fromBottom = Math.max(this.actions.length - 1 - index, 0)
      return (fromBottom * 40) + 'ms'
    }
  }
}
</script>

<style lang="scss" scoped>
.v2026-fab-root {
  position: fixed;
  right: 5vw;
  bottom: 4vh;
  z-index: 999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  pointer-events: none;
}

.v2026-fab-handle,
.v2026-fab-main,
.v2026-fab-actions-item,
.v2026-fab-sliver {
  pointer-events: auto;
}

.v2026-fab-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  transition:
    transform 180ms cubic-bezier(0.4, 0, 0.2, 1),
    opacity 60ms linear 0ms,
    visibility 0s linear 0ms;
}

.v2026-fab-wrapper.is-collapsed {
  transform: translateX(calc(5vw + 56px));
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition:
    transform 180ms cubic-bezier(0.4, 0, 0.2, 1),
    opacity 60ms linear 120ms,
    visibility 0s linear 180ms;
}

.v2026-fab-dock {
  position: relative;
  order: 2;
  display: flex;
}

.v2026-fab-actions {
  order: 1;
  list-style: none;
  margin: 0 0 2vh 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.v2026-fab-actions:empty {
  margin: 0;
}

.v2026-fab-actions-item {
  position: relative;
  display: flex;
  align-items: center;
}

.v2026-fab-actions-item + .v2026-fab-actions-item {
  margin-top: 2vh;
}

.v2026-fab-main {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #ffffff;
  box-shadow: 0 10px 10px rgba(0, 0, 0, 0.2), 0 4px 4px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: filter 140ms ease-out;
}

.v2026-fab-main:hover,
.v2026-fab-main:focus {
  filter: brightness(0.88);
}

.v2026-fab-main-glyph {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  font-size: 24px;
  line-height: 1;
  color: #ffffff;
  transition: transform 200ms ease-in-out;
}

.v2026-fab-main.is-open .v2026-fab-main-glyph {
  transform: rotate(-45deg);
}

.v2026-fab-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #ffffff;
  box-shadow: 0 10px 10px rgba(0, 0, 0, 0.2), 0 4px 4px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: filter 140ms ease-out;
}

.v2026-fab-action:hover,
.v2026-fab-action:focus {
  filter: brightness(0.92);
}

.v2026-fab-action-glyph {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  font-size: 18px;
  line-height: 1;
  color: #ffffff;
}

.v2026-fab-tooltip {
  position: absolute;
  right: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  z-index: 1001;
  padding: 5px 10px 4px;
  border-radius: 0;
  background: #333333;
  color: #ffffff;
  font-size: 12px;
  line-height: 1.4;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 140ms ease-out, visibility 0s linear 140ms;
}

.v2026-fab-actions-item:hover .v2026-fab-tooltip,
.v2026-fab-action:focus-visible ~ .v2026-fab-tooltip {
  opacity: 1;
  visibility: visible;
  transition: opacity 140ms ease-out, visibility 0s linear 0s;
}

.v2026-fab-handle {
  position: absolute;
  left: -20px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid #ebedf0;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.15);
  cursor: pointer;
  transition: background-color 140ms ease-out, transform 140ms ease-out;
}

.v2026-fab-handle:hover {
  background: #e91e63;
  transform: translateY(-50%) scale(1.08);
}

.v2026-fab-handle-glyph {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  line-height: 1;
  color: #e91e63;
  transition: color 140ms ease-out;
}

.v2026-fab-handle:hover .v2026-fab-handle-glyph {
  color: #ffffff;
}

.v2026-fab-sliver {
  position: fixed;
  right: 0;
  bottom: 4vh;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 56px;
  padding: 0;
  border: 0;
  border-radius: 12px 0 0 12px;
  background: #e91e63;
  box-shadow: -2px 0 8px rgba(16, 24, 40, 0.15);
  cursor: pointer;
  transition: width 140ms ease-out, background-color 140ms ease-out;
}

.v2026-fab-sliver:hover {
  width: 32px;
  background: #c2185b;
}

.v2026-fab-sliver-glyph {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 1;
  color: #ffffff;
}

.v2026-fab-main:focus-visible,
.v2026-fab-action:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #c2185b;
}

.v2026-fab-handle:focus-visible,
.v2026-fab-sliver:focus-visible {
  outline: 2px solid var(--v2026-color-main, #31c1d7);
  outline-offset: 2px;
}

.v2026-fab-pill-enter,
.v2026-fab-pill-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.v2026-fab-pill-enter-active,
.v2026-fab-pill-leave-active {
  transition:
    opacity 160ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 160ms cubic-bezier(0.4, 0, 0.2, 1);
}

.v2026-fab-handle-enter,
.v2026-fab-handle-leave-to {
  opacity: 0;
  transform: translate(-10px, -50%);
}

.v2026-fab-handle-enter-active,
.v2026-fab-handle-leave-active {
  transition:
    opacity 160ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 160ms cubic-bezier(0.4, 0, 0.2, 1);
}

@media (max-width: 820px) {
  .v2026-fab-handle {
    left: -22px;
    width: 32px;
    height: 32px;
  }

  .v2026-fab-sliver {
    width: 28px;
    height: 64px;
  }

  .v2026-fab-sliver:hover {
    width: 36px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .v2026-fab-wrapper,
  .v2026-fab-wrapper.is-collapsed,
  .v2026-fab-main,
  .v2026-fab-main-glyph,
  .v2026-fab-action,
  .v2026-fab-tooltip,
  .v2026-fab-handle,
  .v2026-fab-handle-glyph,
  .v2026-fab-sliver,
  .v2026-fab-pill-enter-active,
  .v2026-fab-pill-leave-active,
  .v2026-fab-handle-enter-active,
  .v2026-fab-handle-leave-active {
    transition-duration: 0.01ms;
    transition-delay: 0s;
    animation: none;
  }
}
</style>
